/**
 * Undoing an accidental break (PRP-SKETCH-FIELD-3 J7, H5).
 *
 * While a wall segment is dragged (`beginWallDrag`, then `dragSegmentPreview` in
 * place of `dragWall`), its line snaps to the line of the segment on either side
 * when within `break_snap_in`. Released while snapped (`endSegmentDrag`), the
 * extra corners go and the walls merge into one, openings kept at their world
 * positions. `removeBreak` does the same in one tap for a segment that is colinear
 * with a neighbour or within `break_snap_in` of its line.
 */
import { POLICY, inToFt, type SnapKind } from './policy';
import { EPS, RECT_CORNERS, axisOf, dist, edgeOf, findRoom, tidy, vIdx } from './geom';
import { beginWallDrag, cleanupZero, dragWall, roughenChanged, type WallDrag } from './edit';
import { wallSnapTarget } from './wallSnap';
import { jambStop, splitMissingAtJunctions } from './wallMeetsOpening';
import { wallFtOf } from './thickness';
import type { Axis, Corner, Fail, Plan, Pt, Room } from './types';

/** The line of a parallel neighbour segment, one connector away. */
export interface BreakLine {
  /** The neighbour's coordinate on the drag axis, feet. */
  at: number;
  /** The neighbour segment's ends, for the snap line. */
  a: Pt;
  b: Pt;
}

const reach = () => inToFt(POLICY.break_snap_in);
const normalOf = (a: Pt, b: Pt): Axis | null => {
  const t = axisOf(a, b);
  return t === 'h' ? 'y' : t === 'v' ? 'x' : null;
};

/**
 * The lines of the segments either side of wall `vid`: the wall before (after) it,
 * when parallel and directly colinear, or parallel one connector (a wall across
 * the line, or a zero-length one) away.
 */
export function breakNeighbours(r: Room, vid: number): BreakLine[] {
  const i = vIdx(r, vid), n = r.corners.length;
  if (i < 0) return [];
  const c = (k: number) => r.corners[((k % n) + n) % n];
  const ax = normalOf(c(i), c(i + 1));
  if (!ax) return [];
  const out: BreakLine[] = [];
  const s = c(i), e = c(i + 1);
  // The same wall runs the same way; the room's opposite wall runs back.
  const take = (p: Corner, q: Corner) => {
    if (normalOf(p, q) === ax && (q.x - p.x) * (e.x - s.x) + (q.y - p.y) * (e.y - s.y) > 0) out.push({ at: p[ax], a: p, b: q });
  };
  const across = (p: Corner, q: Corner) => { const t = axisOf(p, q); return t === 'z' || normalOf(p, q) !== ax; };
  // Before: (i-1 -> i) colinear, or (i-2 -> i-1) beyond a connector (i-1 -> i).
  if (normalOf(c(i - 1), c(i)) === ax) take(c(i - 1), c(i));
  else if (across(c(i - 1), c(i))) take(c(i - 2), c(i - 1));
  // After: (i+1 -> i+2) colinear, or (i+2 -> i+3) beyond a connector (i+1 -> i+2).
  if (normalOf(c(i + 1), c(i + 2)) === ax) take(c(i + 1), c(i + 2));
  else if (across(c(i + 1), c(i + 2))) take(c(i + 2), c(i + 3)); // policy-literal-ok: corner index offset
  return out;
}

export interface SegmentPreview {
  /** The segment sits on a neighbour's line (the screen draws `line` and ticks once). */
  snapped: boolean;
  line?: [Pt, Pt];
  /** FIELD-4 K9: the wall sits on another room's corner, wall line or matching size (`line` is the guide). */
  k9?: SnapKind;
  /** FIELD-4 K7: the wall stopped at a door's or window's jamb; the message to show (empty: a flush snap). */
  jamb?: string;
}

/** `dragWall`, then the J7 snap: within `break_snap_in` of a neighbour's line, onto it. */
export function dragSegmentPreview(plan: Plan, drag: WallDrag, delta: Pt): SegmentPreview {
  const pv = segmentSnap(plan, drag, delta);
  // K7: a wall never ends inside a door or window: it stops at the nearer jamb.
  const r = findRoom(plan, drag.rid);
  const stop = r ? jambStop(plan, drag.rid, new Set(drag.vids), drag.ax, wallFtOf(r, drag.vid)) : null;
  if (!r || !stop) return pv;
  drag.vids.forEach((id) => { const q = r.corners.find((c) => c.id === id); if (q) q[drag.ax] += stop.shift; });
  return { snapped: false, jamb: stop.message };
}

/** `dragWall`, then the J7 break snap or the K9 snaps. */
function segmentSnap(plan: Plan, drag: WallDrag, delta: Pt): SegmentPreview {
  dragWall(plan, drag, delta);
  const r = findRoom(plan, drag.rid);
  const v = r?.corners.find((q) => q.id === drag.vid);
  if (!r || !v) return { snapped: false };
  let best: BreakLine | null = null;
  for (const l of breakNeighbours(r, drag.vid)) {
    const off = Math.abs(l.at - v[drag.ax]);
    if (off <= reach() + EPS && (!best || off < Math.abs(best.at - v[drag.ax]))) best = l;
  }
  if (!best) {
    // K9 (another room's corner, wall line or size), measured from where the finger is.
    const raw = drag.start[drag.vids.indexOf(drag.vid)] + (drag.ax === 'x' ? delta.x : delta.y);
    const k = wallSnapTarget(plan, drag.rid, drag.vid, drag.ax, raw);
    if (!k) return { snapped: false };
    drag.vids.forEach((id) => { const q = r.corners.find((c) => c.id === id); if (q) q[drag.ax] = k.at; });
    return { snapped: false, k9: k.kind, line: k.guide };
  }
  const at = best.at;
  drag.vids.forEach((id) => { const q = r.corners.find((c) => c.id === id); if (q) q[drag.ax] = at; });
  const E = edgeOf(r, drag.vid)!;
  const pts = [best.a, best.b, E.a, E.b];
  const ox: Axis = drag.ax === 'x' ? 'y' : 'x';
  const lo = Math.min(...pts.map((p) => p[ox])), hi = Math.max(...pts.map((p) => p[ox]));
  const p0 = { x: 0, y: 0 }, p1 = { x: 0, y: 0 };
  p0[drag.ax] = p1[drag.ax] = at;
  p0[ox] = lo;
  p1[ox] = hi;
  return { snapped: true, line: [p0, p1] };
}

/**
 * Release. Zero-length walls merge (as `endWallDrag`); when the segment ended on
 * a neighbour's line (`snapped`), the corners left colinear at its ends go too and
 * the walls merge. Returns removed-corner -> kept-corner ids for the selection.
 */
export function endSegmentDrag(plan: Plan, drag: WallDrag, snapped: boolean): Map<number, number> {
  const r = findRoom(plan, drag.rid);
  if (!r) return new Map();
  roughenChanged(r, drag); // FIELD-4 K8
  const merged = cleanupZero(r);
  if (snapped) {
    const start = merged.get(drag.vid) ?? drag.vid;
    const i = vIdx(r, start);
    const end = i >= 0 ? r.corners[(i + 1) % r.corners.length].id : null;
    for (const id of [end, start]) if (id != null) mergeAt(r, id, merged);
  }
  tidy(plan);
  splitMissingAtJunctions(plan); // FIELD-4 K7
  return merged;
}

/**
 * Remove corner `id` when the walls either side of it are one straight line: the
 * wall before it runs on, its openings move onto it at the same world position,
 * and a measurement on the line's axis follows the corner before it.
 */
export function mergeAt(r: Room, id: number, merged: Map<number, number> = new Map()): boolean {
  const n = r.corners.length;
  const i = vIdx(r, id);
  if (i < 0 || n <= RECT_CORNERS) return false;
  const p = r.corners[(i - 1 + n) % n], c = r.corners[i], q = r.corners[(i + 1) % n];
  const t = axisOf(p, c);
  if ((t !== 'h' && t !== 'v') || axisOf(c, q) !== t || (c.x - p.x) * (q.x - c.x) + (c.y - p.y) * (q.y - c.y) <= 0) return false;
  const shift = dist(p, c);
  r.openings.forEach((o) => { if (o.edgeStart === c.id) { o.edgeStart = p.id; o.offFt += shift; } });
  const lineAx: Axis = t === 'h' ? 'y' : 'x';
  r.measurements = r.measurements
    .map((m) => (m.a === c.id || m.b === c.id) && m.axis === lineAx
      ? { ...m, a: m.a === c.id ? p.id : m.a, b: m.b === c.id ? p.id : m.b } : m)
    .filter((m) => m.a !== c.id && m.b !== c.id && m.a !== m.b);
  if (r.walls) delete r.walls[c.id];
  r.corners.splice(i, 1);
  merged.forEach((to, from) => { if (to === c.id) merged.set(from, p.id); });
  merged.set(c.id, p.id);
  return true;
}

/** The segment can lose its break in one tap (colinear with a neighbour, or within `break_snap_in`). */
export function canRemoveBreak(plan: Plan, rid: string, vid: number): boolean {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  const ax = E ? normalOf(E.a, E.b) : null;
  return !!(r && E && ax && breakNeighbours(r, vid).some((l) => Math.abs(l.at - E.a[ax]) <= reach() + EPS));
}

/**
 * "Remove break" (J7): segment `vid` goes onto the nearest neighbour line within
 * `break_snap_in` (refused like a wall drag when that wall is shared or measured)
 * and merges with it. One undo step. Returns the merged wall's start corner.
 */
export function removeBreak(
  plan: Plan, rid: string, vid: number,
): { ok: true; vid: number; merged: Map<number, number> } | Fail<'missing' | 'no-break' | 'shared' | 'measured' | 'not-straight'> {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  const ax = E ? normalOf(E.a, E.b) : null;
  if (!r || !E || !ax) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const near = breakNeighbours(r, vid)
    .map((l) => ({ l, off: Math.abs(l.at - E.a[ax]) }))
    .filter((x) => x.off <= reach() + EPS)
    .sort((x, y) => x.off - y.off)[0];
  if (!near) return { ok: false, err: 'no-break', message: 'No break to remove here' };
  let drag: WallDrag | null = null;
  if (near.off > EPS) {
    const b = beginWallDrag(plan, rid, vid);
    if (!b.ok) return b;
    drag = b.drag;
    drag.vids.forEach((id) => { const q = r.corners.find((c) => c.id === id); if (q) q[ax] = near.l.at; });
  }
  const merged = endSegmentDrag(plan, drag ?? { kind: 'wall', rid, vid, ax, vids: [], start: [] }, true);
  const kept = merged.get(drag?.vid ?? vid) ?? drag?.vid ?? vid;
  return { ok: true, vid: kept, merged };
}
